import React from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Modal, ModalContent, ModalOverlay} from '@chakra-ui/react'

import {Box, Text, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {COOKIE_POLICY_URL} from '../../constants'

/**
 * CookieConsentBanner is a React component that displays a banner
 * for handling user consent regarding cookies.
 *
 * This component is typically used to inform users about cookie usage
 * and to obtain their consent. It takes a handler function as a prop
 * to manage the user's consent response.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.handleCookieConsent - A function to handle the user's cookie consent action.
 * @returns {JSX.Element} The rendered CookieConsentBanner component.
 */

const CookieConsentBanner = ({handleCookieConsent}) => {
    const intl = useIntl()
    return (
        <Modal
            isOpen={true}
            onClose={() => {}}
            isCentered
            closeOnOverlayClick={false}
            closeOnEsc={false}
            trapFocus={true}
        >
            <ModalOverlay />
            <ModalContent
                backgroundColor={'white'}
                padding={{base: '30px 10px', md: '30px 20px'}}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                border={'2px solid black'}
                borderRadius={0}
                maxWidth={{base: 'calc(100% - 20px)', md: 'calc(100% - 40px)'}}
                mx={{base: '10px', md: '20px'}}
                role="region"
                aria-label={intl.formatMessage({
                    id: 'cookie_banner_consent.aria-label',
                    defaultMessage: 'Cookie consent banner'
                })}
            >
                <Button
                    alignSelf={'flex-end'}
                    textTransform={'uppercase'}
                    p={0}
                    height={'fit-content'}
                    mb={'40px'}
                    onClick={() => handleCookieConsent(false)}
                    variant={'link'}
                >
                    {intl.formatMessage({
                        id: 'cookie_banner_consent.decline',
                        defaultMessage: 'Continue without accepting'
                    })}
                </Button>
                <Text variant={'bodyBase2'} mb={'40px'} textAlign={'center'}>
                    {intl.formatMessage({
                        id: 'cookie_banner_consent.text',
                        defaultMessage: `To provide the best possible shopping experience and personalized advertising and content, 
                        Vilebrequin uses cookies to store information. By clicking ‘Accept All’, you are accepting our`
                    })}{' '}
                    <Link href={COOKIE_POLICY_URL}>
                        {intl.formatMessage({
                            id: 'cookie_banner_consent.link',
                            defaultMessage: 'Cookie Policy.'
                        })}
                    </Link>
                </Text>
                <Box>
                    <Button
                        onClick={() => handleCookieConsent(true)}
                        variant={'primary'}
                        mr={'10px'}
                    >
                        {intl.formatMessage({
                            id: 'cookie_banner_consent.consent',
                            defaultMessage: 'Accept all'
                        })}
                    </Button>
                    <Button
                        onClick={() => handleCookieConsent(false)}
                        variant={'secondary'}
                        as={Link}
                        href={COOKIE_POLICY_URL}
                    >
                        {intl.formatMessage({
                            id: 'cookie_banner_consent.settings',
                            defaultMessage: 'Settings'
                        })}
                    </Button>
                </Box>
            </ModalContent>
        </Modal>
    )
}

CookieConsentBanner.propTypes = {
    handleCookieConsent: PropTypes.func.isRequired
}

export default CookieConsentBanner
